import apiService from '../apiService';
import { CHUNKED_UPLOAD_THRESHOLD, uploadInChunks } from './chunkedUpload';

/**
 * B2-backed case file API (/api/files/*) — replaces the old gdriveService /
 * ElFinder connector. Nodes are addressed by uuid; the axios interceptor's
 * uuid→id transform means uuids arrive on the client under the `id` key, so
 * every `id` handled here is the server-side uuid.
 */
const rethrow = (error) => {
  throw error?.response?.data ?? error;
};

const fileService = {
  async getTree(fileableType, fileableId) {
    try {
      const response = await apiService.get('/files/tree', {
        params: { fileable_type: fileableType, fileable_uuid: fileableId },
      });
      return response.data.data;
    } catch (error) {
      rethrow(error);
    }
  },

  async createFolder({ fileableType, fileableId, parentId = null, name }) {
    try {
      const response = await apiService.post('/files/folder', {
        fileable_type: fileableType,
        fileable_uuid: fileableId,
        parent_uuid: parentId,
        name,
      });
      return response.data.data;
    } catch (error) {
      rethrow(error);
    }
  },

  async uploadFile({ fileableType, fileableId, parentId = null, file, onProgress }) {
    try {
      if (file.size > CHUNKED_UPLOAD_THRESHOLD) {
        return await uploadInChunks({ fileableType, fileableId, parentId, file, onProgress, http: apiService });
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('fileable_type', fileableType);
      formData.append('fileable_uuid', fileableId);
      if (parentId) formData.append('parent_uuid', parentId);

      const response = await apiService.post('/files', formData);
      return response.data.data;
    } catch (error) {
      rethrow(error);
    }
  },

  async getPreview(id) {
    try {
      const response = await apiService.get(`/files/${id}/preview`);
      return response.data.data; // { url, inline, mime, name }
    } catch (error) {
      rethrow(error);
    }
  },

  async getDownloadUrl(id) {
    try {
      const response = await apiService.get(`/files/${id}/download`);
      return response.data.data.url;
    } catch (error) {
      rethrow(error);
    }
  },

  async rename(id, name) {
    try {
      const response = await apiService.patch(`/files/${id}`, { name });
      return response.data.data;
    } catch (error) {
      rethrow(error);
    }
  },

  async move(id, parentId = null) {
    try {
      const response = await apiService.post(`/files/${id}/move`, {
        parent_uuid: parentId,
      });
      return response.data.data;
    } catch (error) {
      rethrow(error);
    }
  },

  async remove(id) {
    try {
      const response = await apiService.delete(`/files/${id}`);
      return response.data;
    } catch (error) {
      rethrow(error);
    }
  },

  async restoreNode(id) {
    try {
      const response = await apiService.post(`/files/${id}/restore`);
      return response.data;
    } catch (error) {
      rethrow(error);
    }
  },

  async getVersions(id) {
    try {
      const response = await apiService.get(`/files/${id}/versions`);
      return response.data.data;
    } catch (error) {
      rethrow(error);
    }
  },

  async uploadVersion(id, file) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiService.post(`/files/${id}/versions`, formData);
      return response.data.data;
    } catch (error) {
      rethrow(error);
    }
  },

  async restoreVersion(id, versionId) {
    try {
      const response = await apiService.post(`/files/${id}/versions/${versionId}/restore`);
      return response.data;
    } catch (error) {
      rethrow(error);
    }
  },

  async getPermissions(id) {
    try {
      const response = await apiService.get(`/files/${id}/permissions`);
      return response.data.data; // { restricted, grants }
    } catch (error) {
      rethrow(error);
    }
  },

  async setPermissions(id, { restricted, grants = [] }) {
    try {
      const response = await apiService.put(`/files/${id}/permissions`, {
        restricted,
        grants,
      });
      return response.data;
    } catch (error) {
      rethrow(error);
    }
  },
};

export default fileService;
